import { roundNumber } from "./roundNumber";

const MAX_FORECAST_LENGTH = 720;
const PRODUCTION_CUTOFF = 3;

export const calculateMultiSegmentForecast = async (well, fluid, params) => {
  try {
    let forecast = [];
    let eur = {};

    let newFluidParams = {};
    if (params) {
      newFluidParams = params;
    } else {
      newFluidParams = well?.params?.[fluid];
    };

    if (isNaN(newFluidParams?.dlim)) {
      newFluidParams = {...newFluidParams, dlim: 0.01};
    };

    if (newFluidParams) {
      
      const res = await calculateFirstSegment( // Calculate first segment
        well?.production?.byFluid?.[fluid] || [],
        newFluidParams?.ti,
        newFluidParams?.q1i,
        newFluidParams?.di,
        newFluidParams?.dlim,
        newFluidParams?.b1,
        (well?.params?.cutoff || PRODUCTION_CUTOFF)
      );

      if (!res) return {};

      const { firstSegmentForecast, isMultiSegment } = res;

      if (!firstSegmentForecast) return {};
  
      forecast = forecast.concat(firstSegmentForecast);

      if (!well?.params?.[fluid]?.plateau) {
        const historicAverage = await calculateHistoricAverage(well?.production?.byFluid?.[fluid], well?.params?.[fluid]?.ti);
        if (historicAverage) {
          forecast = historicAverage.concat(forecast);
        };
      } else {
        const plateauSegment = calculatePlateauSegment(firstSegmentForecast[0]?.volume?.p50, newFluidParams.ti, new Date(firstSegmentForecast[0]?.date));
        if (plateauSegment) {
          forecast = plateauSegment.concat(forecast);
        };
      }; 

      if (isMultiSegment) {
        const t2 = firstSegmentForecast[firstSegmentForecast?.length - 1]?.monthsOnline;
        newFluidParams = {...newFluidParams, t2};
        const res = await calculateSecondSegment(forecast, newFluidParams?.b2, newFluidParams?.d2lim, (well?.params?.cutoff || PRODUCTION_CUTOFF));
        if (res) {
          const { secondSegmentForecast, isThirdSegment } = res;
          if (secondSegmentForecast) {
            forecast = forecast.concat(secondSegmentForecast);
          };
          if (isThirdSegment) {
            newFluidParams = {...newFluidParams, t3: secondSegmentForecast[secondSegmentForecast?.length - 1]?.monthsOnline};
            const thirdSegmentForecast = await calculateThirdSegment(forecast, newFluidParams?.b3, (well?.params?.cutoff || PRODUCTION_CUTOFF));
            forecast = forecast.concat(thirdSegmentForecast);
          };
        };
      } else {
        newFluidParams = {...newFluidParams, t2: undefined, t3: undefined};
      };
      
      if (forecast?.length > 0) {
        const startCum = well?.production?.byFluid?.[fluid]?.filter(p => p.monthsOnline >= newFluidParams.ti)?.[0]?.cumVolume;
        forecast = calculateProbabilities(forecast, newFluidParams.ti, startCum);
        eur = calculateEur(forecast, well?.production?.byFluid?.[fluid], (well?.params?.cutoff || PRODUCTION_CUTOFF), newFluidParams.ti); // Cutoff
      };

      return { newFluidParams, forecast, eur };
    };
    
  } catch (error) {
    console.log('Could not calculate multisegment forecast');
    console.log(error);
    return;
  };
};

export const updateMultiSegmentForecast = async (well, fluid, param, value) => {
  try {
    let newWell = {...well};
    let fluidParams = {...newWell?.params?.[fluid]};
    if (!newWell || !fluidParams) return {};
    if (param === 'cutoff') {
      newWell.params = {...newWell.params, cutoff: value};
    } else {
      fluidParams[param] = value;
      newWell.params = {...newWell.params, [fluid]: fluidParams};
    };

    const res = await calculateMultiSegmentForecast(newWell, fluid, fluidParams);
    if (res) {
      return res;
    };
    return {};
  } catch (error) {
    console.log('Could not update multi segment forecast');
    console.log(error);
    return {};
  };
};

async function calculateFirstSegment(production, startMonth, q1i, di, dlim, b1, cutoff) {
  try {
    let firstSegmentForecast = [];
    // const endMonth = FORECAST_LENGTH;
    let month = 1;
    let d = 999999;
    let startProduction = production?.filter(p => p.monthsOnline >= startMonth)?.[0]; // Get the production month corresponding to forecast startDate
    let startDate = startProduction?.date ? new Date(startProduction?.date) : new Date(); // Starting date of forecast

    let isMultiSegment = false;
    const equation = b1 > 0 ? 'hyperbolic' : 'exponential';
    let latestProduction = 999;

    while (latestProduction >= cutoff && month <= MAX_FORECAST_LENGTH && !isMultiSegment) {
    // while (month <= endMonth && !isMultiSegment) {

      const production = calculateProduction(equation, q1i, di, b1, month);
      
      if (equation === 'hyperbolic') {
        d = calculateDeclineRate(di, b1, month);
        if (d < dlim) {
          isMultiSegment = true; // if decline rate is lower than dlim, switch to multisegment mode
        };
      };

      if (startDate.getDate() === 27 || startDate.getDate() === 28 || startDate.getDate() === 29 || startDate.getDate() === 30 || startDate.getDate() === 31) {
        startDate.setDate(2);
        startDate.setMonth(startDate.getMonth() + 1);
      };
      startDate = new Date(Date.UTC(startDate.getFullYear(), startDate.getMonth(), 2));

      const monthlyForecast = {
        date: startDate?.toISOString()?.split('T')?.[0],
        monthsOnline: (startMonth + month - 1),
        volume: {
          p10: (production.q * 1.25) || 0,
          p50: production.q || 0,
          p90: (production.q * 0.8) || 0
        },
        cumVolume: {
          p10: 0,
          p50: 0,
          p90: 0 
        },
        d: equation === 'hyperbolic' ? d : di
      };

      firstSegmentForecast.push(monthlyForecast);
      latestProduction = production.q;
      month++;
      startDate = new Date(new Date(startDate)?.setMonth(new Date(startDate)?.getMonth() + 1));
    };

    return { firstSegmentForecast, isMultiSegment };
  } catch (error) {
    console.log('Could not calculate first segment');
    console.log(error);
    return null;
  };
};

async function calculateSecondSegment(forecast, b2, d2lim, cutoff) {
  try {
    if (!forecast || forecast.length === 0) return;
    let secondSegmentForecast = [];
    let month = 1;
    let d = 999999;
    const startProduction = forecast[forecast.length - 1]; // Get forecast corresponding to last month of first segment
    let startDate = new Date(startProduction?.date); // Starting date of forecast
    startDate = new Date(new Date(startDate)?.setMonth(new Date(startDate)?.getMonth() + 1));

    const q2i = startProduction?.volume?.p50;
    const d2i = startProduction?.d;
    const equation = b2 > 0 ? 'hyperbolic' : 'exponential';
    let isThirdSegment = false;
    
    let latestProduction = 999;

    while (latestProduction >= cutoff && month <= MAX_FORECAST_LENGTH && !isThirdSegment) {
      const production = calculateProduction(equation, q2i, d2i, b2, month);
      
      if (equation === 'hyperbolic') {
        d = calculateDeclineRate(d2i, b2, month);
        if (d < d2lim) {
          isThirdSegment = true; // if decline rate is lower than d2lim, add third segment
        };
      };

      if (startDate.getDate() === 27 || startDate.getDate() === 28 || startDate.getDate() === 29 || startDate.getDate() === 30 || startDate.getDate() === 31) {
        startDate.setDate(2);
        startDate.setMonth(startDate.getMonth() + 1);
      };
      startDate = new Date(Date.UTC(startDate.getFullYear(), startDate.getMonth(), 2));

      const monthlyForecast = {
        date: startDate?.toISOString()?.split('T')?.[0],
        monthsOnline: startProduction.monthsOnline + (month),
        volume: {
          p10: (production.q * 1.25),
          p50: production.q || 0,
          p90: (production.q * 0.8)
        },
        cumVolume: {
          p10: 0,
          p50: 0,
          p90: 0
        },
        d: equation === 'hyperbolic' ? d : d2i
      };

      secondSegmentForecast.push(monthlyForecast);
      latestProduction = production.q;
      month++;
      startDate = new Date(new Date(startDate)?.setMonth(new Date(startDate)?.getMonth() + 1));
    };

    if (secondSegmentForecast.length === 0) return null;

    return { secondSegmentForecast, isThirdSegment };

  } catch (error) {
    console.log('Could not calculate second segment forecast');
    console.log(error);
    return null;
  };
};

async function calculateThirdSegment(forecast, b3, cutoff) {
  try {
    let thirdSegmentForecast = [];
    let month = 1;
    const startProduction = forecast[forecast.length - 1]; // Get forecast corresponding to last month of first segment
    let startDate = new Date(startProduction?.date); // Starting date of forecast
    startDate = new Date(new Date(startDate)?.setMonth(new Date(startDate)?.getMonth() + 1));

    const q3i = startProduction?.volume?.p50;
    const d3i = startProduction?.d;
    const equation = b3 > 0 ? 'hyperbolic' : 'exponential'; 

    let latestProduction = 999;

    while (latestProduction >= cutoff && month <= MAX_FORECAST_LENGTH) {
      const production = calculateProduction(equation, q3i, d3i, b3, month);
     
      if (startDate.getDate() === 27 || startDate.getDate() === 28 || startDate.getDate() === 29 || startDate.getDate() === 30 || startDate.getDate() === 31) {
        startDate.setDate(2);
        startDate.setMonth(startDate.getMonth() + 1);
      };
      startDate = new Date(Date.UTC(startDate.getFullYear(), startDate.getMonth(), 2));

      const monthlyForecast = {
        date: startDate?.toISOString()?.split('T')?.[0],
        monthsOnline: startProduction?.monthsOnline + month,
        volume: {
          p10: (production.q * 1.25),
          p50: production.q || 0,
          p90: (production.q * 0.8)
        },
        cumVolume: {
          p10: 0,
          p50: 0,
          p90: 0
        }
      };
      thirdSegmentForecast.push(monthlyForecast);
      latestProduction = production.q;
      month++;
      startDate = new Date(new Date(startDate)?.setMonth(new Date(startDate)?.getMonth() + 1));
    };

    if (thirdSegmentForecast.length === 0) return null;

    return thirdSegmentForecast;

  } catch (error) {
    console.log('Could not calculate third segment forecast');
    console.log(error);
    return null;
  };

};

function calculatePlateauSegment(q1i, startMonth, startDate) {
  try {
    
    let plateauSegment = [];

    let month = 1;

    let delta = startMonth - month;

    let currentDate = new Date(startDate.setMonth(new Date(startDate).getMonth() - delta)); // get the first starting date

    for (let i = 0; i < delta; i++) {

      if (currentDate.getDate() === 27 || currentDate.getDate() === 28 || currentDate.getDate() === 29 || currentDate.getDate() === 30 || currentDate.getDate() === 31) {
        currentDate.setDate(2);
        currentDate.setMonth(currentDate.getMonth() + 1);
      };
      currentDate = new Date(Date.UTC(currentDate.getFullYear(), currentDate.getMonth(), 2));

      plateauSegment.push({
        date: currentDate?.toISOString()?.split('T')?.[0],
        monthsOnline: month,
        volume: {
          p10: q1i,
          p50: q1i,
          p90: q1i
        },
        cumVolume: {
          p10: 0,
          p50: 0,
          p90: 0 
        },
        d: 0
      });
      month++;
      currentDate = new Date(new Date(currentDate)?.setMonth(new Date(currentDate).getMonth() + 1)); // add 1 month
    };

    plateauSegment.forEach((p,i) => {
      let cumVolume = 0;
      if (i === 0) {
        cumVolume = p.volume.p50;
      } else {
        cumVolume = plateauSegment[i-1]?.cumVolume?.p50 + p.volume.p50;
      };
      plateauSegment[i].cumVolume = {p10: cumVolume, p50: cumVolume, p90: cumVolume};
    });

    return plateauSegment;
  } catch (error) {
    console.log(error);  
  };
};

async function calculateHistoricAverage(production, forecastStartMonth) {
  try {
    if (!production || !production.length > 0) return [];

    let historicAverage = [];

    let smoothedProduction = [];
    let smoothedCumProduction = [];


    production.forEach(p => {
      if (p.monthsOnline < forecastStartMonth) {
        smoothedProduction.push(Number(p.volume));
        smoothedCumProduction.push(Number(p.cumVolume));
      };
    });

    const deltaProd = production.length - smoothedProduction.length;

    if (deltaProd > 0) {
      smoothedProduction = smoothedProduction.concat(production.slice((production.length - deltaProd - 1), ).map(p => p.volume));
    };

    for (let i = 0; i < production.length; i++) {
      if (production[i].monthsOnline < forecastStartMonth) {
        const date = production?.[i]?.date;

        const prod = Number(smoothedProduction[i]);

        historicAverage.push({
          date,
          monthsOnline: (production[i].monthsOnline),
          volume: {
            p10: prod,
            p50: prod,
            p90: prod
          },
          cumVolume: {
            p10: (i === 0 ? 0 : historicAverage[i-1]?.cumVolume?.p10) + prod,
            p50: (i === 0 ? 0 : historicAverage[i-1]?.cumVolume?.p50) + prod,
            p90: (i === 0 ? 0 : historicAverage[i-1]?.cumVolume?.p90) + prod
          },
          d: 0
        });
      };
    };

    return historicAverage;

  } catch (error) {
    console.log(error);
    return null;
  };
};

function calculateDeclineRate(di, b, t) {
  try {
    const ai = 1 / b * (Math.pow((1 - di), -b) - 1); // convert secant decline to nominal
    return ai / (1 + b * ai * t);
  } catch (error) {
    console.log(error);
  };
};

function calculateProbabilities(forecast, startMonth, startCum) {
  try {
    let filteredForecast = forecast.filter(f => f.monthsOnline > startMonth);
    const length = filteredForecast?.length - 1;
    let newForecast = [...forecast];

    newForecast.forEach((d,i) => {
      if (d.monthsOnline >= startMonth) {
        newForecast[i].volume.p10 = (d.volume.p50 + (d.volume.p50 * (0.25 * ((d.monthsOnline - startMonth) / length))));
        newForecast[i].volume.p90 = (d.volume.p50 - (d.volume.p50 * (0.2 * ((d.monthsOnline - startMonth) / length))));
      };
    });

    newForecast.forEach((d,i) => {
      if (d.monthsOnline >= startMonth) {
        newForecast[i].cumVolume.p10 = Math.max((newForecast[i-1]?.cumVolume.p10 || 0) + d.volume.p10, startCum);
        newForecast[i].cumVolume.p50 = Math.max((newForecast[i-1]?.cumVolume.p50 || 0)+ d.volume.p50, startCum);
        newForecast[i].cumVolume.p90 = Math.max((newForecast[i-1]?.cumVolume.p90 || 0)+ d.volume.p90, startCum);
      };

    });

    return newForecast;
  } catch (error) {
    console.log('Could not calculate probabilities');
    console.log(error);
    return forecast;
  };
};

function calculateProduction (equation, qi, di, b, month) {
  try {
    let q = 0;
    let Q = 0;
    if (equation === 'hyperbolic') {
      q = parseFloat(qi / (1.0 + ((1.0 - di) ** (-b) - 1) * month) ** (1.0 / b)); // using secant decline rate
      // q = parseFloat((qi / Math.pow((1.0 + b * di * month), (1.0 / b))));
    } else if (equation === 'exponential') {
      q = parseFloat(qi * Math.exp(Math.log(1.0 - di) * month)); // using secant decline rate
      // q = parseFloat(qi * Math.exp(-di * month));
    };
    q = roundNumber(q);
    return { q, Q };
    
  } catch (error) {
    console.log(error);
  };
};

export const calculateEur = (forecast, production, cutoff=3, ti) => {
  try {
    let eur = {
      p10: {
        monthsOnline: null,
        date: null,
        volume: null,
        cumVolume: null
      },
      p50: {
        monthsOnline: null,
        date: null,
        volume: null,
        cumVolume: null
      },
      p90: {
        monthsOnline: null,
        date: null,
        volume: null,
        cumVolume: null
      },
    };

    const lastProd = production?.find(p => p.monthsOnline === ti);
    const lastDate = new Date(lastProd?.date)?.getTime() || Date.now();
    const cumVolToDate = lastProd?.cumVolume;
    const probs = ['p10', 'p50', 'p90'];
    
    for (let prob of probs) {
      const forecastPortion = forecast.filter(f => new Date(f.date).getTime() >= lastDate && f.volume?.[prob] >= cutoff);
      const lastForecast = forecast?.slice(-1)?.[0];

      eur[prob].monthsOnline = forecastPortion?.slice(-1)?.[0]?.monthsOnline || lastForecast?.monthsOnline;;
      eur[prob].date = forecastPortion?.slice(-1)?.[0]?.date || lastForecast?.date;
      eur[prob].volume = forecastPortion?.slice(-1)?.[0]?.volume?.[prob] || lastForecast?.volume?.[prob];
      eur[prob].cumVolume = forecastPortion?.reduce((tot, curr) => curr.volume?.[prob] + tot, cumVolToDate) || lastForecast?.cumVolume?.[prob];
    };
    return eur;
  } catch (error) {
    console.log('Could not calculate EUR');
    console.log(error);
    return null;
  };
};
